import { useCameraSwitch } from "@features/camera-preview";
import { ShareButton } from "@features/screen-share";
import { ownerOf, type Call } from "@entities/call";
import { Permission, has } from "@entities/role";
import { Tile, type Person } from "@entities/user";
import type { CallMember } from "@entities/workspace";
import type { Uuid } from "@shared/api";
import { Hint } from "@shared/ui/bits";
import {
  IconCall,
  IconCamera,
  IconClose,
  IconExpand,
  IconLeaveCall,
  IconMic,
  IconMicOff,
} from "@shared/ui/icons";

/**
 * Звонок в переписке — полоса над лентой, как в Discord: лица тех, кто в
 * звонке, и тех, кому звонят, под ними — кнопки. Переписка остаётся под
 * полосой: звонок в личке — часть разговора, а не отдельный экран.
 *
 * Кнопки зависят от того, где мы:
 * - в звонке — микрофон, камера, показ экрана и «отключиться»;
 * - звонят нам — «с камерой», «принять» и «отклонить»;
 * - звонок идёт без нас — «с камерой» и «присоединиться»: вышедшему не звонят
 *   заново, он возвращается сам.
 *
 * Камера и показ на полосе не видны — их смотрят на развёрнутом звонке
 * (кнопка в углу), там же сетка, сцена и полный экран.
 */
export function CallStrip({
  channelId,
  channelName,
  call,
  members,
  ringing,
  people,
  meId,
  permissions,
  onJoin,
  onDecline,
  onExpand,
}: {
  channelId: Uuid;
  /** Имя переписки — для выбора источника показа. */
  channelName: string;
  call: Call;
  /** Кто в звонке. */
  members: CallMember[];
  /** Кому звонят сейчас, кроме тех, кто уже в звонке. */
  ringing: Uuid[];
  /** Лица участников переписки, с собой. */
  people: Person[];
  meId: Uuid;
  permissions: number;
  /** Войти; `video` — сразу с камерой. */
  onJoin: (video: boolean) => void;
  onDecline: () => void;
  onExpand: () => void;
}) {
  const camera = useCameraSwitch(call);
  const here = call.channelId === channelId && (call.state === "live" || call.state === "joining");
  const ringsMe = !here && ringing.includes(meId);
  const canSpeak = has(permissions, Permission.Speak);
  const canVideo = has(permissions, Permission.Video);
  const speaking = new Set(call.peers.filter((peer) => peer.speaking).map((peer) => ownerOf(peer.identity)));

  const byId = new Map(people.map((person) => [person.id, person]));
  const faces = [
    ...members.map((member) => ({ id: member.id, ringing: false, muted: !member.microphone })),
    ...ringing
      .filter((id) => !members.some((member) => member.id === id))
      .map((id) => ({ id, ringing: true, muted: false })),
  ].filter((face) => byId.has(face.id));

  return (
    // Материал панелей, как у списка каналов (`pane-surface`), без стекла движка.
    <div className="call-strip pane-surface" role="region" aria-label="Звонок">

      <Hint label="Развернуть звонок" side="left">
        <button type="button" className="call-strip-expand" aria-label="Развернуть звонок" onClick={onExpand}>
          <IconExpand size={15} />
        </button>
      </Hint>

      <div className="call-strip-faces">
        {faces.map((face) => {
          const person = byId.get(face.id)!;
          return (
            <Hint
              key={face.id}
              label={face.ringing ? `${person.name} — звоним` : person.name}
              side="bottom"
            >
              <span
                className={`call-face ${face.ringing ? "ringing" : ""} ${speaking.has(face.id) ? "speaking" : ""}`}
              >
                <Tile person={person} size={80} mark={null} />
                {face.muted && !face.ringing && (
                  <span className="call-face-muted" aria-label="Микрофон выключен">
                    <IconMicOff size={12} />
                  </span>
                )}
              </span>
            </Hint>
          );
        })}
      </div>

      <div className="call-strip-bar">
        {here ? (
          <>
            <span className="call-strip-group">
              {canSpeak && (
                <Hint label={call.microphone ? "Выключить микрофон" : "Включить микрофон"}>
                  <button
                    type="button"
                    className={`call-ctl ${call.microphone ? "" : "off"}`}
                    onClick={() => void call.toggleMicrophone()}
                  >
                    <span className="call-ctl-glyph">
                      {call.microphone ? <IconMic size={17} /> : <IconMicOff size={17} />}
                    </span>
                  </button>
                </Hint>
              )}
              {canVideo && (
                <>
                  <Hint label={call.camera ? "Выключить камеру" : "Включить камеру"}>
                    <button type="button" className={`call-ctl ${call.camera ? "on" : ""}`} onClick={camera.toggle}>
                      <span className="call-ctl-glyph">
                        <IconCamera size={17} />
                      </span>
                    </button>
                  </Hint>
                  {camera.preview}
                  <ShareButton call={call} channelName={channelName} serverName="личная переписка" />
                </>
              )}
            </span>
            <Hint label="Отключиться">
              <button type="button" className="call-answer decline" aria-label="Отключиться" onClick={() => void call.leave()}>
                <IconLeaveCall size={20} />
              </button>
            </Hint>
          </>
        ) : (
          <>
            {canVideo && (
              <Hint label="Присоединиться с камерой">
                <button
                  type="button"
                  className="call-answer video"
                  aria-label="Присоединиться с камерой"
                  disabled={call.state === "joining"}
                  onClick={() => onJoin(true)}
                >
                  <IconCamera size={20} />
                </button>
              </Hint>
            )}
            <Hint label={ringsMe ? "Принять" : "Присоединиться к звонку"}>
              <button
                type="button"
                className="call-answer accept"
                aria-label={ringsMe ? "Принять" : "Присоединиться к звонку"}
                disabled={call.state === "joining"}
                onClick={() => onJoin(false)}
              >
                <IconCall size={20} />
              </button>
            </Hint>
            {ringsMe && (
              <Hint label="Отклонить">
                <button type="button" className="call-answer decline" aria-label="Отклонить" onClick={onDecline}>
                  <IconClose size={20} />
                </button>
              </Hint>
            )}
          </>
        )}
      </div>

      {call.problem && (call.channelId === channelId || call.state === "failed") && (
        <div className="call-strip-problem" role="alert">
          {call.problem}
        </div>
      )}
    </div>
  );
}
